// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnInit,
  Input,
  OnDestroy,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';

import { TranslateModule } from '@ngx-translate/core';

import { SharedModule } from 'src/app/modules/shared/shared.module';
import { CtsGantryInitStatus } from '../../interfaces/cts-gantry-init-status';
import { CtsGantryInitEventComponent } from '../cts-gantry-init-event/cts-gantry-init-event.component';
import { SettingsService } from 'src/app/modules/shared/services/settings.service';
import { CtstatusService } from '../../services/ctstatus.service';
import { APICallGateComponent } from 'src/app/modules/shared/components/api-call-gate.component';
import { SystemDetailsModel } from 'src/app/api/remote-api/.portal.generated/models';
import { Subject, switchMap, takeUntil } from 'rxjs';
import { ScreenLayoutService } from 'src/app/modules/shared/services/screen-layout-service/screen-layout.service';
import { CtsGantryTimezoneSelectComponent } from '../cts-gantry-timezone-select/cts-gantry-timezone-select.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    SharedModule,
    TranslateModule,
    APICallGateComponent,
    CtsGantryInitEventComponent,
    CtsGantryTimezoneSelectComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-cts-gantry-init-status',
  templateUrl: './cts-gantry-init-status.component.html',
  styleUrl: './cts-gantry-init-status.component.scss',
})
export class CtsGantryInitStatusComponent implements OnInit, OnDestroy {
  @Input({ required: true }) systemDetails!: SystemDetailsModel;
  private readonly settingsService = inject(SettingsService);
  protected initStatus: CtsGantryInitStatus | undefined;
  protected gantryEventList = this.ctsystemService.gantryEventListInitStatus;
  private readonly unsubscribe = new Subject<void>();

  public readonly INFORMATIVE_SOM5_SOM7_ERROR_STRING =
    'No support for CT Som5 and Som7.';
  public readonly INFORMATIVE_NO_SOFTWARE_FAMILY_DETECTED =
    'No software family detected. Please contact SeSO Support.';

  protected modalMobileVisible = true;
  protected modalMobileConfirmed = sessionStorage.getItem(
    '@ctStatusMobileDisclaimer',
  );

  public constructor(
    protected readonly ctsystemService: CtstatusService,
    protected readonly screenLayoutService: ScreenLayoutService,
  ) {
    this.handleEventList();
  }

  public handleEventList(): void {
    this.gantryEventList.lastSuccessfulData$
      .pipe(
        takeUntil(this.unsubscribe),
        switchMap((res) => {
          this.initEventsSubscribeFn(res as unknown as CtsGantryInitStatus);
          return this.settingsService.timelineAscending$;
        }),
      )
      .subscribe(this.sortEvents);
  }

  ngOnInit(): void {
    this.requestGantryEventList();
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private initEventsSubscribeFn(initStatus: CtsGantryInitStatus): void {
    if (initStatus) {
      this.initStatus = initStatus;
    }
  }

  private requestGantryEventList() {
    this.gantryEventList.request({
      MaterialNumber: this.systemDetails.systemMaterialNumber,
      SerialNumber: this.systemDetails.systemSerialNumber,
    });
  }

  public readonly sortEvents = (ascending: boolean): void => {
    this.initStatus?.events.sort((a, b) => {
      const dateA = a.timestamp;
      const dateB = b.timestamp;
      return ascending
        ? dateA.localeCompare(dateB)
        : dateB.localeCompare(dateA);
    });
  };

  protected onClickConfirmModalMobile = (): void => {
    this.modalMobileVisible = false;
    sessionStorage.setItem('@ctStatusMobileDisclaimer', 'Confirm mobile modal');
  };
}
